import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCancelSubscriptionMutation,
  useSubscriptionStateQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { useSubscriptionStore } from 'src/store/subscriptionStore';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as CheckmarkSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/checkmark.svg';
import { ReactComponent as CancellationConfirmedSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/cancellation/cancellation-confirmed.svg';
import { toast } from 'src/utils/appAlert';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './Cancellation.scss';

const CANCEL_REASONS = [
  'Too expensive',
  'Not using enough',
  'Missing features I need',
  'Switching to another tool',
  'Other',
];

const formatDate = (isoString?: string | null) => {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
};

export const CancelSubscription: FC = () => {
  const navigate = useNavigate();
  const { data: subState } = useSubscriptionStateQuery();
  const { mutate: cancelSub, isPending } = useCancelSubscriptionMutation();
  const { cancelSubscription: storeCancel, accessEndsDate: storeAccessEndsDate } =
    useSubscriptionStore();

  const [selectedReason, setSelectedReason] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [dynamicAccessDate, setDynamicAccessDate] = useState<string | null>(null);

  const displayAccessDate =
    dynamicAccessDate ||
    formatDate(subState?.access_until || subState?.current_period_end) ||
    storeAccessEndsDate ||
    'the end of your billing cycle';

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleKeepPro = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleBackToSub = () => {
    navigate('/onboarding-trainer/dashboard/subscription/my-subscription', { replace: true });
  };

  const handleReactivate = () => {
    navigate('/onboarding-trainer/dashboard/subscription/reactivate', { replace: true });
  };

  const handleConfirmCancellation = () => {
    if (!selectedReason || isPending) {
      if (!selectedReason) {
        toast.error('Please select a reason for cancelling.');
      }
      return;
    }

    cancelSub(
      { reason: selectedReason },
      {
        onSuccess: (res) => {
          const formattedAccess = res.access_until ? formatDate(res.access_until) : undefined;
          storeCancel(selectedReason, formattedAccess || undefined);
          if (res.access_until) {
            setDynamicAccessDate(formatDate(res.access_until));
          }
          toast.success(res.message || 'Your subscription has been cancelled.');
          setIsConfirmed(true);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to cancel subscription.');
        },
      },
    );
  };

  return (
    <div className="cancellation-page-container">
      {!isConfirmed ? (
        <>
          {/* Header */}
          <div className="cancellation-header">
            <button type="button" className="cancel-back-btn" onClick={handleBack}>
              <UserChevronLeftSVG />
              <span>{'Cancel Subscription'}</span>
            </button>
            <p className="cancel-subtitle">
              {"We're sorry to see you go. Please let us know why you're cancelling."}
            </p>
          </div>

          <div className="cancellation-card-wrapper">
            <div className="reasons-form-box">
              <h3>{"What's the reason for cancelling?"}</h3>

              <div className="reasons-list">
                {CANCEL_REASONS.map((reason) => {
                  const isSelected = selectedReason === reason;
                  return (
                    <div
                      key={reason}
                      className={`reason-option-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedReason(reason)}
                    >
                      <span className="reason-text">{reason}</span>
                      {isSelected && (
                        <div className="reason-check-wrap">
                          <CheckmarkSVG className="gold-check" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Before You Go Card */}
              <div className="before-you-go-box">
                <h4>{'Before you go...'}</h4>
                <div className="benefits-checklist">
                  <div className="benefit-item">
                    <CheckmarkSVG className="orange-check" />
                    <span>{`Your Pro access will continue until ${displayAccessDate}`}</span>
                  </div>
                  <div className="benefit-item">
                    <CheckmarkSVG className="orange-check" />
                    <span>{'You can reactivate anytime.'}</span>
                  </div>
                  <div className="benefit-item">
                    <CheckmarkSVG className="orange-check" />
                    <span>{'All your data and programs will be saved'}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="cancellation-actions-row">
                <button
                  type="button"
                  className="btn-keep-pro"
                  onClick={handleKeepPro}
                  disabled={isPending}
                >
                  {'Keep Pro Access'}
                </button>
                <button
                  type="button"
                  className="btn-confirm-cancel"
                  onClick={handleConfirmCancellation}
                  disabled={!selectedReason || isPending}
                >
                  {isPending ? (
                    <>
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 14, color: '#fff', marginRight: 8 }}
                            spin
                          />
                        }
                      />
                      <span>{'Cancelling...'}</span>
                    </>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* State 2: Cancellation Confirmed */
        <div className="cancellation-confirmed-view">
          <div className="confirmed-icon-wrap">
            <CancellationConfirmedSVG />
          </div>

          <h2>{'Cancellation confirmed'}</h2>
          <p className="confirmed-subtext">
            {`Your subscription has been cancelled. You will continue to have Pro access until ${displayAccessDate}`}
          </p>

          <div className="confirmed-actions">
            <button type="button" className="btn-back-to-sub" onClick={handleBackToSub}>
              <UserChevronLeftSVG />
              <span>{'Back to subscription'}</span>
            </button>
            <button type="button" className="btn-reactivate-dark" onClick={handleReactivate}>
              {'Reactivate Pro'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
